/*
 * Atelier — Swatch Hover Swap
 *
 * Phase 7 / P7-D2 (May 4, 2026): extracted from
 * collection_atelier.js.liquid + search_atelier.js.liquid into this
 * shared file. Both PLP types (collection grid, search results grid)
 * share the same swatch-hover behavior; the prior duplication caused
 * the search version to silently drift behind the collection version
 * (e.g. the collection version had hover-intent + multi-swatch group
 * exit handling + defaultPrevented checks that search lacked).
 *
 * This file IS the polished version (collection's). Sections that need
 * it (`collection-atelier.liquid`, `collection-atelier-28.liquid`,
 * `search_main.liquid`) include this as a separate `<script>` tag
 * loaded with `defer`. It auto-initializes on DOMContentLoaded.
 *
 * Behavior summary:
 * - Hovering a `.linked-swatch` inside a `.product-card` (after a
 *   120ms hover-intent delay) swaps the card's product + model images
 *   to the swatch's `data-plp-product-img` / `data-plp-model-img`
 *   variants and rewrites the description text.
 * - Mouseout reverts UNLESS the cursor moved to another swatch in the
 *   same `.inline-swatch-group`, OR onto the "+N see more" link in
 *   that group.
 * - Click selects the swatch (sets `is-current`), prevents navigation,
 *   updates the card's main link href / aria-label / quick-view URL /
 *   "see more" target / price display.
 * - Keyboard: focusin/focusout mirror hover; allows tab navigation.
 * - Modifier-clicks (cmd/ctrl/shift/alt) and middle-clicks fall through
 *   to native open-in-new-tab behavior.
 */
class AtelierSwatchHoverSwap {
	constructor() {
		this.grid = document.querySelector('.atelier-grid');
		this.wrapper = document.querySelector('.atelier-collection');
		this.activeCard = null;

		// Hover intent: reduce accidental swaps/downloads when the cursor passes over swatches quickly.
		this.hoverDelayMs = 120;
		this._hoverTimer = null;
		this._hoverPending = { card: null, swatch: null };
	}

	init() {
		if (!this.grid) return;

		// Prevent layout jumps: clamp the material label to one line.
		this.grid.querySelectorAll('.description-under-swatches').forEach((el) => {
			try {
				if (!el.dataset.fullText) el.dataset.fullText = (el.textContent || '').replace(/\u00A0/g, ' ');
				this.setDescText(el, el.dataset.fullText);
			} catch (e) {}
		});

		// Hover events (desktop)
		if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
			this.grid.addEventListener('mouseover', (e) => this.onOver(e));
			this.grid.addEventListener('mouseout', (e) => this.onOut(e));
		}

		// Keyboard accessibility
		this.grid.addEventListener('focusin', (e) => this.onFocus(e));
		this.grid.addEventListener('focusout', (e) => this.onBlur(e));

		// Click = select swatch (do not navigate)
		this.grid.addEventListener('click', (e) => this.onClick(e));
	}

	clearHoverTimer() {
		if (this._hoverTimer) {
			clearTimeout(this._hoverTimer);
			this._hoverTimer = null;
		}
		this._hoverPending = { card: null, swatch: null };
	}

	getMode() {
		// Respect the existing Atelier toggle (images-product vs images-model)
		if (this.wrapper && this.wrapper.classList.contains('images-model')) return 'model';
		return 'product';
	}

	getCardFromSwatch(swatch) {
		return swatch.closest('product-card') || swatch.closest('.product-card');
	}

	ensureDescStyle(el) {
		if (!el) return;
		// Inline styles win over theme CSS and prevent wrapping.
		el.style.display = 'block';
		el.style.whiteSpace = 'nowrap';
		el.style.overflow = 'hidden';
		el.style.textOverflow = 'ellipsis';
		el.style.minWidth = '0';
		el.style.maxWidth = '100%';
	}

	truncateLabel(text, maxChars) {
		const raw = (text || '').replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
		if (!raw) return '';
		if (!maxChars || raw.length <= maxChars) return raw;
		const cut = Math.max(0, maxChars - 3);
		return raw.slice(0, cut).trimEnd() + '...';
	}

	setDescText(el, fullText) {
		if (!el) return;
		this.ensureDescStyle(el);
		const maxChars = (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) ? 26 : 34;
		const clean = (fullText || '').replace(/\u00A0/g, ' ');
		el.dataset.fullText = clean;
		el.setAttribute('title', clean.trim());
		el.textContent = this.truncateLabel(clean, maxChars);
	}

	rememberOriginal(card) {
		if (!card || card.__swatchOrig) return;
		const pick = (sel) => {
			const el = card.querySelector(sel);
			if (!el) return null;
			return {
				el,
				src: el.getAttribute('src') || '',
				srcset: el.getAttribute('srcset') || '',
				dataSrcset: el.getAttribute('data-srcset') || '',
				currentSrc: el.currentSrc || ''
			};
		};
		const pickText = (sel) => {
			const el = card.querySelector(sel);
			if (!el) return null;
			return {
				el,
				text: el.dataset.fullText || el.textContent || ''
			};
		};
		const pickLink = () => {
			// Main link is the direct child anchor of product-card
			let el = null;
			if (card.matches && card.matches('product-card')) {
				el = card.querySelector(':scope > a.product-card-url');
			}
			if (!el) {
				const links = card.querySelectorAll('a.product-card-url');
				links.forEach((a) => {
					if (!el && a.parentElement === card) el = a;
				});
			}
			return el
				? {
						el,
						href: el.getAttribute('href') || '',
						ariaLabel: el.getAttribute('aria-label') || ''
					}
				: null;
		};
		const pickDataUrl = (sel, attr) => {
			const el = card.querySelector(sel);
			if (!el) return null;
			return { el, value: el.getAttribute(attr) || '' };
		};
		const pickPrice = () => {
			const finalEl = card.querySelector('.price .final');
			const compareEl = card.querySelector('.price .compare');
			return {
				finalEl,
				finalText: finalEl ? finalEl.textContent || '' : '',
				compareEl,
				compareText: compareEl ? compareEl.textContent || '' : '',
				compareHasHide: compareEl ? compareEl.classList.contains('hide') : false
			};
		};
		card.__swatchOrig = {
			product: pick('img.product-image'),
			model: pick('img.model-image'),
			desc: pickText('.description-under-swatches'),
			mainLink: pickLink(),
			quickView: pickDataUrl('.quick-view-trigger', 'data-url'),
			seeMore: pickDataUrl('.inline-swatch-group .see-more', 'href'),
			price: pickPrice()
		};
	}

	setImg(imgInfo, url, srcset = '') {
		if (!imgInfo || !imgInfo.el || (!url && !srcset)) return;
		const el = imgInfo.el;
		// Avoid churn if already set
		const currentSrc = el.getAttribute('src') || '';
		const currentSrcset = el.getAttribute('srcset') || '';
		if (url && currentSrc === url && (!srcset || currentSrcset === srcset)) return;

		if (url) el.setAttribute('src', url);
		if (srcset) {
			el.setAttribute('srcset', srcset);
			el.setAttribute('data-srcset', srcset);
		} else if (url) {
			// Fallback for older callers: keep something in srcset/data-srcset
			el.setAttribute('srcset', url);
			el.setAttribute('data-srcset', url);
		}
	}

	apply(card, swatch) {
		if (!card || !swatch) return;
		this.rememberOriginal(card);

		const swatchLabel = swatch.dataset.plpSwatchLabel;
		if (swatchLabel && card.__swatchOrig && card.__swatchOrig.desc && card.__swatchOrig.desc.el) {
			this.setDescText(card.__swatchOrig.desc.el, swatchLabel);
		}

		const productUrl = swatch.dataset.plpProductImg || '';
		const modelUrl = swatch.dataset.plpModelImg || productUrl;
		const productSrcset = swatch.dataset.plpProductSrcset || '';
		const modelSrcset = swatch.dataset.plpModelSrcset || productSrcset;

		if (!productUrl && !productSrcset) return;

		// Swap the PLP card images (product + model). Use srcset when available for crisp hover.
		this.setImg(card.__swatchOrig.product, productUrl, productSrcset);
		this.setImg(card.__swatchOrig.model, modelUrl, modelSrcset);

		this.activeCard = card;
	}

	applySelectionMeta(card, swatch) {
		if (!card || !swatch) return;
		if (!card.__swatchOrig) this.rememberOriginal(card);

		const url = swatch.dataset.plpUrl;
		const aria = swatch.dataset.plpAriaLabel;
		const price = swatch.dataset.plpPrice;
		const compare = swatch.dataset.plpCompare || '';
		const onSale = (swatch.dataset.plpOnSale || '') === 'true';

		// Update main link (image/title click target)
		if (url && card.__swatchOrig.mainLink && card.__swatchOrig.mainLink.el) {
			card.__swatchOrig.mainLink.el.setAttribute('href', url);
		}
		if (aria && card.__swatchOrig.mainLink && card.__swatchOrig.mainLink.el) {
			card.__swatchOrig.mainLink.el.setAttribute('aria-label', aria);
		}

		// Update quick view button target
		if (url && card.__swatchOrig.quickView && card.__swatchOrig.quickView.el) {
			card.__swatchOrig.quickView.el.setAttribute('data-url', url);
		}

		// Update "see more" link target
		if (url && card.__swatchOrig.seeMore && card.__swatchOrig.seeMore.el) {
			card.__swatchOrig.seeMore.el.setAttribute('href', url);
		}

		// Update price display
		if (card.__swatchOrig.price) {
			const p = card.__swatchOrig.price;
			if (p.finalEl && price) p.finalEl.textContent = price;
			if (p.compareEl) {
				if (onSale && compare) {
					p.compareEl.textContent = compare;
					p.compareEl.classList.remove('hide');
				} else {
					p.compareEl.textContent = compare;
					p.compareEl.classList.add('hide');
				}
			}
		}
	}

	applySelected(card) {
		if (!card || !card.__swatchSelectedEl) return;
		this.apply(card, card.__swatchSelectedEl);
		this.applySelectionMeta(card, card.__swatchSelectedEl);
	}

	restore(card) {
		if (!card || !card.__swatchOrig) return;
		const restoreOne = (info) => {
			if (!info || !info.el) return;
			const el = info.el;
			// Prefer restoring srcset if available, otherwise restore src/currentSrc.
			if (info.dataSrcset) el.setAttribute('data-srcset', info.dataSrcset);
			if (info.srcset) el.setAttribute('srcset', info.srcset);
			const src = info.currentSrc || info.src;
			if (src) el.setAttribute('src', src);
		};

		restoreOne(card.__swatchOrig.product);
		restoreOne(card.__swatchOrig.model);

		if (card.__swatchOrig.desc && card.__swatchOrig.desc.el) {
			this.setDescText(card.__swatchOrig.desc.el, card.__swatchOrig.desc.text || '');
		}

		if (card.__swatchOrig.mainLink && card.__swatchOrig.mainLink.el) {
			if (card.__swatchOrig.mainLink.href) card.__swatchOrig.mainLink.el.setAttribute('href', card.__swatchOrig.mainLink.href);
			if (card.__swatchOrig.mainLink.ariaLabel) card.__swatchOrig.mainLink.el.setAttribute('aria-label', card.__swatchOrig.mainLink.ariaLabel);
		}
		if (card.__swatchOrig.quickView && card.__swatchOrig.quickView.el) {
			card.__swatchOrig.quickView.el.setAttribute('data-url', card.__swatchOrig.quickView.value || '');
		}
		if (card.__swatchOrig.seeMore && card.__swatchOrig.seeMore.el) {
			card.__swatchOrig.seeMore.el.setAttribute('href', card.__swatchOrig.seeMore.value || '');
		}
		if (card.__swatchOrig.price) {
			const p = card.__swatchOrig.price;
			if (p.finalEl) p.finalEl.textContent = p.finalText || '';
			if (p.compareEl) {
				p.compareEl.textContent = p.compareText || '';
				if (p.compareHasHide) p.compareEl.classList.add('hide');
				else p.compareEl.classList.remove('hide');
			}
		}
	}

	revertCard(card) {
		if (!card) return;
		if (card.__swatchSelectedEl) this.applySelected(card);
		else this.restore(card);
	}

	onOver(e) {
		const swatch = e.target.closest && e.target.closest('.inline-swatch-group .linked-swatch');
		if (!swatch || !this.grid.contains(swatch)) return;

		// Avoid re-triggering when moving inside the same swatch
		if (swatch.contains(e.relatedTarget)) return;

		const card = this.getCardFromSwatch(swatch);
		if (!card) return;

		// If moving between cards, restore the previous active card first
		if (this.activeCard && this.activeCard !== card) {
			this.revertCard(this.activeCard);
		}

		// Hover intent: schedule apply after a short delay
		this.clearHoverTimer();
		this._hoverPending = { card, swatch };
		this._hoverTimer = setTimeout(() => {
			// Ensure the pending target is still the same and still in DOM
			if (!this._hoverPending || this._hoverPending.card !== card || this._hoverPending.swatch !== swatch) return;
			if (!document.body.contains(swatch) || !document.body.contains(card)) return;
			this.apply(card, swatch);
			this.clearHoverTimer();
		}, this.hoverDelayMs);
	}

	onOut(e) {
		const swatch = e.target.closest && e.target.closest('.inline-swatch-group .linked-swatch');
		if (!swatch || !this.grid.contains(swatch)) return;

		// Still inside swatch → ignore
		if (swatch.contains(e.relatedTarget)) return;

		const card = this.getCardFromSwatch(swatch);
		if (!card) return;

		// If moving to another swatch within the same card/group, don't restore in between.
		const related = e.relatedTarget;
		if (related && related.closest) {
			const nextSwatch = related.closest('.inline-swatch-group .linked-swatch');
			if (nextSwatch) {
				const nextCard = this.getCardFromSwatch(nextSwatch);
				if (nextCard && nextCard === card) {
					// Cancel any pending timer for the previous swatch; next swatch over will schedule its own.
					if (this._hoverPending && this._hoverPending.swatch === swatch) this.clearHoverTimer();
					return;
				}
			}
			// Also treat hovering the "+N" see-more link as still being inside swatch group.
			const group = swatch.closest('.inline-swatch-group');
			if (group && group.contains(related) && this.getCardFromSwatch(group) === card) {
				if (this._hoverPending && this._hoverPending.swatch === swatch) this.clearHoverTimer();
				return;
			}
		}

		// Leaving swatches area → cancel pending and revert
		if (this._hoverPending && this._hoverPending.card === card) this.clearHoverTimer();
		this.revertCard(card);
		if (this.activeCard === card) this.activeCard = null;
	}

	onFocus(e) {
		const swatch = e.target.closest && e.target.closest('.inline-swatch-group .linked-swatch');
		if (!swatch || !this.grid.contains(swatch)) return;
		const card = this.getCardFromSwatch(swatch);
		if (!card) return;
		this.clearHoverTimer();
		this.apply(card, swatch);
	}

	onBlur(e) {
		const swatch = e.target.closest && e.target.closest('.inline-swatch-group .linked-swatch');
		if (!swatch || !this.grid.contains(swatch)) return;
		const card = this.getCardFromSwatch(swatch);
		if (!card) return;
		this.clearHoverTimer();
		this.revertCard(card);
		if (this.activeCard === card) this.activeCard = null;
	}

	onClick(e) {
		const swatch = e.target.closest && e.target.closest('.inline-swatch-group .linked-swatch');
		if (!swatch || !this.grid.contains(swatch)) return;

		// Allow "open in new tab/window" behaviors
		if (e.defaultPrevented) return;
		if (e.button !== 0) return;
		if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

		const card = this.getCardFromSwatch(swatch);
		if (!card) return;

		// Select: prevent navigation and persist the card as this linked product
		e.preventDefault();
		this.clearHoverTimer();

		// Update current ring state
		const group = swatch.closest('.inline-swatch-group');
		if (group) {
			group.querySelectorAll('.linked-swatch.is-current').forEach((el) => {
				el.classList.remove('is-current');
				el.setAttribute('aria-current', 'false');
			});
		}
		swatch.classList.add('is-current');
		swatch.setAttribute('aria-current', 'true');

		card.__swatchSelectedEl = swatch;
		this.applySelected(card);
	}
}

// Phase 7 / P7-D2: idempotent init guard. If both the collection PLP
// script and a hypothetical second include path ever load on the same
// page, this prevents double-binding of the grid event listeners.
document.addEventListener('DOMContentLoaded', () => {
	if (window.__atelierSwatchSwapInited) return;
	window.__atelierSwatchSwapInited = true;
	try {
		new AtelierSwatchHoverSwap().init();
	} catch (e) {
		console.error('AtelierSwatchHoverSwap init failed:', e);
	}
});
